import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, Grid, Paper, List, ListItem, ListItemText, Divider } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useNavigate } from 'react-router';
import UserDisplay from '../../components/UserDisplay/UserDisplay';
import Button from "@mui/material/Button";
import Switch from "../../components/Switch";
import {
  useGetUserSettingTasksQuery,
  useUpdateUserPreferencesMutation,
} from "../../services/usersSlice";
import { userTaskSettings } from "../../../shared/types/User";

const UserProfileSettings = () => {
  const navigate = useNavigate();
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();
  const { data: user, refetch } = useGetUserSettingTasksQuery();
  const [hideMyPendingTasks, setHideMyPendingTasks] = useState(false);
  const [hideMyCompleteTasks, setHideMyCompleteTasks] = useState(false);
  const [hideOtherPendingTasks, setHideOtherPendingTasks] = useState(false);
  const [hideOtherCompleteTasks, setHideOtherCompleteTasks] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Remove any stray import or code fragments below

  useEffect(() => {
    if (user && user.userProfileSettings) {
      const settings = typeof user.userProfileSettings === 'string'
        ? JSON.parse(user.userProfileSettings)
        : user.userProfileSettings;
      const {
        hideMyPendingTasks,
        hideMyCompleteTasks,
        hideOtherPendingTasks,
        hideOtherCompleteTasks,
      } = settings || {};
      setHideMyPendingTasks(hideMyPendingTasks);
      setHideMyCompleteTasks(hideMyCompleteTasks);
      setHideOtherPendingTasks(hideOtherPendingTasks);
      setHideOtherCompleteTasks(hideOtherCompleteTasks);
      setIsDarkMode(user.themeMode === "dark");
    }
  }, [user]);

  const taskSettings = {
    hideMyPendingTasks,
    hideMyCompleteTasks,
    hideOtherPendingTasks,
    hideOtherCompleteTasks,
  };

  const updateUserSettings = async () => {
    await updateUserPreferences({
      body: {
        userProfileSettings: taskSettings,
        themeMode: isDarkMode ? "dark" : "light",
      },
    });
    refetch();
  };

  const isDarkModeEnabled = user?.themeMode === "dark";

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        px: { xs: 1.5, sm: 3 },
        py: { xs: 2, sm: 4 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        boxSizing: 'border-box',
      }}
    >
      <Box
          sx={{
            maxWidth: 1400,
            width: '100%',
            mx: 'auto',
            mt: { xs: 1, sm: 2 },
            mb: { xs: 2, sm: 4 },
            borderRadius: 7,
            boxShadow: '0 12px 48px 0 rgba(60,72,100,0.22)',
            px: { xs: 2, sm: 3 },
            py: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0,
            border: '2.5px solid #e3e6ea',
            position: 'relative',
          }}
        >
          <Button
            aria-label="Go back"
            onClick={() => navigate(-1)}
            startIcon={<ChevronLeftIcon />}
            sx={{
              position: 'absolute',
              top: 16,
              left: 24,
              zIndex: 2,
              minWidth: 0,
              px: 0,
              textTransform: 'none',
              fontWeight: typographyTokens.fontWeights.semibold,
              '& .MuiButton-startIcon': { mr: 0.25 },
            }}
          >
            Back
          </Button>
          {/* Light/Dark Mode Toggle at top right of main grid */}
          <Box sx={{ position: 'absolute', top: 16, right: 24, display: 'flex', alignItems: 'center', gap: 1, zIndex: 2 }}>
            <Typography sx={{ fontSize: typographyTokens.fontSizes.md, color: '#888' }}>{isDarkMode ? 'Dark Mode' : 'Light Mode'}</Typography>
            <Box
              onClick={() => setIsDarkMode(!isDarkMode)}
              sx={{
                position: "relative",
                width: 50,
                height: 28,
                borderRadius: 14,
                backgroundColor: isDarkMode ? "#4b5563" : "#e5e7eb",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: 'flex',
                alignItems: 'center',
                px: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: isDarkMode ? "#111827" : "#f9fafb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: typographyTokens.fontSizes.base,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  ml: isDarkMode ? 'calc(100% - 24px)' : 0,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {isDarkMode ? "🌙" : "☀️"}
              </Box>
            </Box>
          </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 0, px: 2, mt: 0, mb: 0 }}>
          {/* Profile Avatar with photo fallback */}
          {user && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                width: 180,
                height: 180,
                mb: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                boxShadow: 3,
                border: '6px solid #fff',
                overflow: 'hidden',
              }}>
                <UserDisplay
                  userID={user.id}
                  user={user}
                  showPicture
                  showName={false}
                  showEmail={false}
                  avatarSx={{ width: 180, height: 180, fontSize: typographyTokens.fontSizes.display }}
                />
              </Box>
            </Box>
          )}
          {/* Name Centered and Bold */}
          {user && (
            <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.h5, textAlign: 'center', mb: 2, letterSpacing: 2, color: '#2372B9' }}>
              {user.displayName || user.givenName || user.familyName}
            </Typography>
          )}
          {/* Details in a neat grid */}
          {user && (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gridGap: 17,
              width: '85%',
              maxWidth: 1600,
              mt: 0.5,
              mb: 2,
              borderRadius: 3,
              p: 2,
              boxShadow: 1,
              border: '1px solid #e3e6ea',
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.lg, mb: 0.5 }}>Email</Typography>
                <Typography sx={{ color: '#2372B9', fontSize: typographyTokens.fontSizes.lg, wordBreak: 'break-all', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{user.email}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.lg, mb: 0.5 }}>Job Title</Typography>
                <Typography sx={{ color: '#2372B9', fontSize: typographyTokens.fontSizes.lg, wordBreak: 'break-word', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{user?.jobTitle}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.lg, mb: 0.5 }}>Department</Typography>
                <Typography sx={{ color: '#2372B9', fontSize: typographyTokens.fontSizes.lg, wordBreak: 'break-word', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{user?.department}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.lg, mb: 0.5 }}>Office Location</Typography>
                <Typography sx={{ color: '#2372B9', fontSize: typographyTokens.fontSizes.lg, wordBreak: 'break-word', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{user?.officeLocation || '—'}</Typography>
              </Box>
            </Box>
          )}
        </Box>
        {/* Settings Section Below */}
        <Box sx={{ width: '100%', mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: typographyTokens.fontWeights.semibold, textAlign: 'center', mb: 2 }}>Settings</Typography>
          <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                <Box display="flex" alignItems="center" mb={1.5}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: typographyTokens.fontSizes.h5Small }} />
                  <Typography variant="subtitle1" fontWeight={typographyTokens.fontWeights.bold} sx={{ fontSize: typographyTokens.fontSizes.lg }}>My Tasks</Typography>
                </Box>
                <Divider />
                <List>
                    <ListItem disableGutters sx={{ pl: 0, pr: 0, minHeight: 36 }}>
                    <ListItemText
                      primary={<span style={{ fontSize: typographyTokens.fontSizes.md }}>Hide Locked Tasks</span>}
                      sx={{ mr: 1 }}
                    />
                    <Switch checked={hideMyPendingTasks} onChange={() => setHideMyPendingTasks(!hideMyPendingTasks)} />
                  </ListItem>
                  <Divider />
                    <ListItem disableGutters sx={{ pl: 0, pr: 0, minHeight: 36 }}>
                    <ListItemText
                      primary={<span style={{ fontSize: typographyTokens.fontSizes.md }}>Hide Complete Tasks</span>}
                      sx={{ mr: 1 }}
                    />
                    <Switch checked={hideMyCompleteTasks} onChange={() => setHideMyCompleteTasks(!hideMyCompleteTasks)} />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
              <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                <Box display="flex" alignItems="center" mb={1.5}>
                  <GroupIcon sx={{ mr: 1, color: 'primary.main', fontSize: typographyTokens.fontSizes.h5Small }} />
                  <Typography variant="subtitle1" fontWeight={typographyTokens.fontWeights.bold} sx={{ fontSize: typographyTokens.fontSizes.lg }}>Tasks for Others</Typography>
                </Box>
                <Divider />
                <List>
                    <ListItem disableGutters sx={{ pl: 0, pr: 0, minHeight: 36 }}>
                    <ListItemText
                      primary={<span style={{ fontSize: typographyTokens.fontSizes.md }}>Hide Locked Tasks</span>}
                      sx={{ mr: 1 }}
                    />
                    <Switch checked={hideOtherPendingTasks} onChange={() => setHideOtherPendingTasks(!hideOtherPendingTasks)} />
                  </ListItem>
                  <Divider />
                    <ListItem disableGutters sx={{ pl: 0, pr: 0, minHeight: 36 }}>
                    <ListItemText
                      primary={<span style={{ fontSize: typographyTokens.fontSizes.md }}>Hide Complete Tasks</span>}
                      sx={{ mr: 1 }}
                    />
                    <Switch checked={hideOtherCompleteTasks} onChange={() => setHideOtherCompleteTasks(!hideOtherCompleteTasks)} />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
              <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={updateUserSettings}
                size="large"
                sx={{
                    mt: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: "none",
                    py: 1,
                  fontSize: typographyTokens.fontSizes.base,
                  fontWeight: typographyTokens.fontWeights.semibold,
                  borderRadius: 2,
                  boxShadow: isDarkModeEnabled
                    ? "0 4px 12px rgba(59, 130, 246, 0.3)"
                    : "0 4px 12px rgba(37, 99, 235, 0.3)",
                  transition: "all 0.3s ease",
                  '&:hover': {
                    transform: "translateY(-2px)",
                    boxShadow: isDarkModeEnabled
                      ? "0 6px 20px rgba(59, 130, 246, 0.4)"
                      : "0 6px 20px rgba(37, 99, 235, 0.4)",
                  },
                  display: 'block',
                  mx: 'auto',
                }}
              >
                Save Settings
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default UserProfileSettings;
